/**
 * Pure helpers shared by the web-search and site-scraper tool renderers.
 *
 * Kept free of React/DOM so they can be unit tested in isolation (see
 * web-tool-renderer-utils.spec.ts).
 */

/** Google's public favicon service. `sz` is the requested icon size in px. */
const FAVICON_ENDPOINT = 'https://www.google.com/s2/favicons';

/**
 * Trimmed string, or undefined when the value isn't a string or is blank.
 * Tool inputs/outputs routinely carry nulls and empty strings.
 */
export function cleanString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Parse a raw URL into a {@link URL}, tolerating a missing scheme (assumes
 * https) and partial/streaming input. Returns undefined when unparseable.
 */
function toUrl(rawUrl: string | null | undefined): URL | undefined {
  if (rawUrl === null || rawUrl === undefined) {
    return undefined;
  }
  const trimmed = rawUrl.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  const candidate = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    return new URL(candidate);
  } catch {
    return undefined;
  }
}

/**
 * Hostname without a leading `www.`, or undefined when the URL can't be
 * parsed.
 */
export function getHostname(
  rawUrl: string | null | undefined
): string | undefined {
  const url = toUrl(rawUrl);
  if (url === undefined) {
    return undefined;
  }
  return url.hostname.replace(/^www\./i, '');
}

/**
 * Human-readable site label. Falls back to the trimmed raw input when the URL
 * isn't parseable yet (e.g. while the tool input is still streaming).
 */
export function getSiteLabel(
  rawUrl: string | null | undefined
): string | undefined {
  const host = getHostname(rawUrl);
  if (host !== undefined) {
    return host;
  }
  const trimmed = rawUrl?.trim();
  return trimmed !== undefined && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Favicon URL for a page/host via Google's favicon service, or undefined when
 * no host can be derived.
 */
export function faviconUrl(
  rawUrl: string | null | undefined,
  size = 64
): string | undefined {
  const host = getHostname(rawUrl);
  if (host === undefined) {
    return undefined;
  }
  const params = new URLSearchParams({ domain: host, sz: String(size) });
  return `${FAVICON_ENDPOINT}?${params.toString()}`;
}

/** Pluralizes a count, e.g. `formatCount(1, 'result')` → "1 result". */
export function formatCount(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** UTF-8 byte length of a string. */
export function byteLengthOf(value: string): number {
  return new TextEncoder().encode(value).length;
}

/**
 * Human-readable byte size, e.g. 512 → "512 B", 16331 → "16 KB",
 * 5_242_880 → "5.0 MB". Non-positive or invalid input renders as "0 B".
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  if (bytes < 1024) {
    return `${Math.round(bytes)} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${Math.round(kb)} KB`;
  }
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}
